import { PropsWithChildren } from 'react';
import { Navigate } from 'react-router';
import { gql, useQuery } from '@apollo/client';
import { useUserContext } from '../../hooks/userContext';
import { Pages } from '../../utils';
import { CircularProgress, Box } from '@mui/material';
import { useEffectiveRole } from '../../hooks/useEffectiveRole';

type HomeownerTier = 'basic' | 'Guild' | 'GuildPlus';
type DesignerTier = 'Home' | 'Design' | 'DesignPlus';
type RequiredTier = HomeownerTier | DesignerTier;

interface TierGuardProps extends PropsWithChildren {
  requiresTier: RequiredTier | RequiredTier[];
  redirectTo?: string;
  showUpgradeModal?: boolean;
  allowAdminOverride?: boolean;
}

interface MembershipStatusQueryResponse {
  getAccountMembershipStatus?: {
    subscription?: string | null;
  } | null;
}

interface MembershipStatusQueryVariables {
  userId: string;
}

const GET_ACCOUNT_MEMBERSHIP_STATUS_QUERY = gql`
  query GetAccountMembershipStatusForTierGuard($userId: ID!) {
    getAccountMembershipStatus(userId: $userId) {
      subscription
    }
  }
`;

/**
 * TierGuard enforces membership tier restrictions.
 * Blocks access to premium features for users without required tier.
 */
export const TierGuard: React.FC<TierGuardProps> = ({ 
  children, 
  requiresTier,
  redirectTo = Pages.DASHBOARD,
  showUpgradeModal = false,
  allowAdminOverride = false,
}) => {
  const { user, loading } = useUserContext();
  const { effectiveTier, isAdmin, canAccessAdminFeatures } = useEffectiveRole();
  const { data: membershipStatusData, loading: membershipStatusLoading } = useQuery<
    MembershipStatusQueryResponse,
    MembershipStatusQueryVariables
  >(GET_ACCOUNT_MEMBERSHIP_STATUS_QUERY, {
    variables: { userId: user?.id || '' },
    skip: !user?.id,
    fetchPolicy: 'cache-and-network',
  });

  const requiredTiers = Array.isArray(requiresTier) ? requiresTier : [requiresTier];


  // Show loading while checking user status
  if (loading || membershipStatusLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress sx={{ color: '#00A06D' }} />
      </Box>
    );
  }

  // If no user, redirect to login
  if (!user) {
    return <Navigate to={Pages.LOGIN} replace />;
  }

  if (allowAdminOverride && canAccessAdminFeatures) {
    return <>{children}</>;
  }

  // Admins bypass tier checks (unless impersonating)
  if (isAdmin) {
    return <>{children}</>;
  }

  // Map subscription to tier
  const getTierFromSubscription = (): string => {
    const membershipSubscription = membershipStatusData?.getAccountMembershipStatus?.subscription;
    const rawSubscription =
      typeof membershipSubscription === 'string' && membershipSubscription.trim()
        ? membershipSubscription
        : typeof user.subscription === 'string'
          ? user.subscription
          : (user.subscription as any)?.homeownerValue ||
            (user.subscription as any)?.designerValue ||
            (user.subscription as any)?.value ||
            (user.subscription as any)?.tier ||
            'basic';

    // Homeowner tiers
    if (rawSubscription === 'Free' || rawSubscription === 'basic') return 'basic';
    if (rawSubscription === 'GuildMonthly' || rawSubscription === 'Guild') return 'Guild';
    if (rawSubscription === 'GuildAnnually' || rawSubscription === 'GuildPlus') return 'GuildPlus';
    if (rawSubscription === 'StyleFileMonthly' || rawSubscription === 'StyleFileAnnually') return 'Guild';

    // Designer tiers (if implemented)
    if (rawSubscription === 'Home' || rawSubscription === 'HomeMonthly' || rawSubscription === 'HomeBiAnnually') {
      return 'Home';
    }
    if (rawSubscription === 'Design' || rawSubscription === 'DesignMonthly' || rawSubscription === 'DesignBiAnnually') {
      return 'Design';
    }
    if (
      rawSubscription === 'DesignPlus' ||
      rawSubscription === 'Design+' ||
      rawSubscription === 'DesignPlusMonthly' ||
      rawSubscription === 'DesignPlusBiAnnually'
    ) {
      return 'DesignPlus';
    }

    return effectiveTier || 'basic';
  };

  const currentTier = getTierFromSubscription();

  // Define tier hierarchy
  const homeownerTierOrder: HomeownerTier[] = ['basic', 'Guild', 'GuildPlus'];
  const designerTierOrder: DesignerTier[] = ['Home', 'Design', 'DesignPlus'];

  const checkTierAccess = (): boolean => {
    // Check if current tier matches any required tier
    if (requiredTiers.includes(currentTier as RequiredTier)) {
      return true;
    }

    // Check if current tier is higher in hierarchy
    for (const requiredTier of requiredTiers) {
      // Check homeowner tiers
      if (homeownerTierOrder.includes(currentTier as HomeownerTier) && 
          homeownerTierOrder.includes(requiredTier as HomeownerTier)) {
        const currentIndex = homeownerTierOrder.indexOf(currentTier as HomeownerTier);
        const requiredIndex = homeownerTierOrder.indexOf(requiredTier as HomeownerTier);
        if (currentIndex >= requiredIndex) {
          return true;
        }
      }

      // Check designer tiers
      if (designerTierOrder.includes(currentTier as DesignerTier) && 
          designerTierOrder.includes(requiredTier as DesignerTier)) {
        const currentIndex = designerTierOrder.indexOf(currentTier as DesignerTier);
        const requiredIndex = designerTierOrder.indexOf(requiredTier as DesignerTier);
        if (currentIndex >= requiredIndex) {
          return true;
        }
      }
    }

    return false;
  };

  const hasAccess = checkTierAccess();


  // Block access if tier insufficient
  if (!hasAccess) {
    
    // TODO: Show upgrade modal if requested
    if (showUpgradeModal) {
      // Future: Trigger upgrade modal
    }
    
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};
